import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { cmToTwips, twipsToCm } from '../../utils/marginsUtils';
import { API_BASE_URL } from '../../../config/ApiConfig';

const MarginsConfigModal = ({ isOpen, onClose, initial, onSaved }) => {
  const [orientacao, setOrientacao] = useState(initial?.orientacao || 'P');
  // defaults for Paisagem (used when no `initial` provided)
  const [top, setTop] = useState('4,50');
  const [bottom, setBottom] = useState('2,00');
  const [left, setLeft] = useState('1,50');
  const [right, setRight] = useState('1,50');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initial) {
      setOrientacao(initial.orientacao || 'P');
      setTop(twipsToCm(initial.top).replace('.', ','));
      setBottom(twipsToCm(initial.bottom).replace('.', ','));
      setLeft(twipsToCm(initial.left).replace('.', ','));
      setRight(twipsToCm(initial.right).replace('.', ','));
    }
  }, [initial]);

  const sanitize = v => v ? v.replace(/\./g, ',').replace(/[^0-9,]/g, '') : '';
  const formatVal = v => {
    const n = parseFloat(v.replace(',', '.'));
    return isNaN(n) ? '0,00' : n.toFixed(2).replace('.', ',');
  };

  const save = async () => {
    setError(null);
    const cnpj = initial?.cnpj || localStorage.getItem('cnpj');
    if (!cnpj) {
      setError('CNPJ não encontrado. Selecione uma entidade antes de salvar.');
      return;
    }
    setLoading(true);
    try {
      const topTwips = cmToTwips(top.replace(',', '.'));
      const bottomTwips = cmToTwips(bottom.replace(',', '.'));
      const leftTwips = cmToTwips(left.replace(',', '.'));
      const rightTwips = cmToTwips(right.replace(',', '.'));

      // Basic validation to avoid accidental swaps or invalid values
      const minTwips = 50; // ~0.09 cm
      const maxTwips = 20000; // safety upper bound (~35 cm)
      if ([topTwips, bottomTwips, leftTwips, rightTwips].some(v => v <= 0 || v < minTwips || v > maxTwips)) {
        setError('Valores de margem inválidos. Verifique os campos e tente novamente.');
        setLoading(false);
        return;
      }

      const payload = {
        cnpj,
        orientacao,
        top: topTwips,
        bottom: bottomTwips,
        left: leftTwips,
        right: rightTwips,
      };
      // keep a console trace to help debugging client-side input issues
      // eslint-disable-next-line no-console
      console.debug('Saving margins payload:', payload);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_BASE_URL.APP_MARGINS}/save`, payload, { headers });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data || 'Erro ao salvar margens.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="md" centered>
      <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
          <h6 className="modal-title text-center m-0">
            <i className="bi bi-rulers me-2"></i>
            Configurar Margens
          </h6>
          <button
            className="btn btn-link text-white p-0 mb-0"
            onClick={onClose}
            title="Fechar"
            style={{ fontSize: '0.9rem', textDecoration: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Orientação</Form.Label>
            <Form.Select value={orientacao} disabled>
              <option value="P">Paisagem (P)</option>
              <option value="R">Retrato (R)</option>
            </Form.Select>
          </Form.Group>

          <div className="row">
            <div className="col-6 mb-2">
              <Form.Label>Superior (cm)</Form.Label>
              <Form.Control value={top} onChange={e=>setTop(sanitize(e.target.value))} onBlur={e=>setTop(formatVal(top))} />
            </div>
            <div className="col-6 mb-2">
              <Form.Label>Inferior (cm)</Form.Label>
              <Form.Control value={bottom} onChange={e=>setBottom(sanitize(e.target.value))} onBlur={e=>setBottom(formatVal(bottom))} />
            </div>
            <div className="col-6 mb-2">
              <Form.Label>Esquerda (cm)</Form.Label>
              <Form.Control value={left} onChange={e=>setLeft(sanitize(e.target.value))} onBlur={e=>setLeft(formatVal(left))} />
            </div>
            <div className="col-6 mb-2">
              <Form.Label>Direita (cm)</Form.Label>
              <Form.Control value={right} onChange={e=>setRight(sanitize(e.target.value))} onBlur={e=>setRight(formatVal(right))} />
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="primary" onClick={save} disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

MarginsConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initial: PropTypes.object,
  onSaved: PropTypes.func,
};

export default MarginsConfigModal;
